import { notify } from '@tiger/bugsnag';
import { useMount, useStates } from '@tiger/hooks';
import Icon from '@tiger/icons';
import { FC } from 'react';
import Modal from 'components/Modal';
import Toast from 'components/Toast';
import type { FileUploaderBucketType } from 'types/common';
import API from 'utils/API';
import classlist from 'utils/classlist';
import http from 'utils/http';
import FileInput from './FileInput';
import { getAcceptsConfig, uploadPic } from './helper';
import pdfIconUrl from './images/pdf-icon.png';
import { IUploaderType } from './types';
import './style.scss';
import { FileItemType } from './UploaderItem';

export interface IFileUploaderProps {
    type: IUploaderType;
    onSuccess?(value: string, name: string, file: File): void;
    onDelete?: () => void;
    name?: string;
    bucket: FileUploaderBucketType | null; // 如果不走auth接口则需要显示传null
    defaultValue?: FileItemType;
}

interface FileUploaderState {
    isUploading: boolean; // 是否正在上传
    isDownloading: boolean; // 是否正在下载
    progress: number;
    error: Error | null;
    localUrl?: string;
    success: boolean;
    filename: string;
    isPDF?: boolean;
}

const isMobile = window.innerWidth < 540;

function checkIsPdf(fileName: string) {
    return /\.pdf$/i.test(fileName);
}

const FileUploader: FC<IFileUploaderProps> = ({ type, onSuccess, onDelete, name, bucket, defaultValue }) => {
    const [{ isUploading, error, progress, success, filename, localUrl, isPDF, isDownloading }, setState] = useStates<
        FileUploaderState,
        {}
    >({
        isUploading: false,
        error: null,
        progress: 0,
        success: false,
        filename: '',
        isDownloading: false
    });

    const fileChange = async (file: File) => {
        try {
            const localUrl = await window.URL.createObjectURL(file);

            setState({
                localUrl
            });

            await uploadFile(file);
        } catch {}
    };

    const uploadFile = async (file: File) => {
        const formdata = new FormData();

        formdata.append('file', file, file.name);
        formdata.append('type', `pic_${name}`);

        // 标记开始上传
        setState({
            isUploading: true,
            progress: 0,
            error: null
        });

        try {
            const resp = bucket
                ? await uploadPic(formdata, bucket, progress => {
                      // 上传进度
                      setState({
                          progress
                      });
                  })
                : null;

            setState({
                success: true,
                filename: file.name,
                isPDF: checkIsPdf(file.name)
            });

            onSuccess?.(resp ? resp.data.url : '', file.name, file);
        } catch (error) {
            notify(error, { filename: file.name, filesize: file.size });

            Toast.show(error);

            setState({
                error,
                success: false
            });
        }

        setState({
            isUploading: false
        });
    };

    const preview = () => {
        Modal.open({
            title: __('预览'),
            className: 'upload-img-preview',
            component: () => <img src={localUrl!} alt="preview" />,
            width: 420,
            closable: true
        });
    };

    const handleDel = () => {
        onDelete?.();
    };

    // 获取背景图片的Url
    const getBackgroundImageUrl = () => {
        if (success) {
            return isPDF ? `url(${pdfIconUrl})` : `url(${localUrl})`;
        }

        return '';
    };

    // 只有加载一次就可以
    useMount(() => {
        async function getImgUrl() {
            const { url: defaultFileUrl, name: defaultFilename } = defaultValue || {};
            /**
             * 从缓存中读取的时候, defaultFileName有值
             * 但需要考虑如果是从接口读取的, 不是从缓存中, defaultName可能没有值
             * 这时候就要依赖 defaultFileUrl来进行判断是否是PDF
             */
            const curFileIsPdf = checkIsPdf(defaultFilename || defaultFileUrl || '');

            // 如果有默认的地址, 但是没有localUrl, 说明此时是已经上传过, 且不是当前页面上传, 需要从服务端拉取图片
            if (defaultFileUrl && !localUrl) {
                // 标记开始下载图片
                setState({
                    isDownloading: true,
                    progress: 0,
                    error: null
                });

                try {
                    const resp: any = await http.get(API.CUSTOMER.getFile(defaultFileUrl || ''), {
                        responseType: 'arraybuffer'
                    });
                    const blob = new Blob([resp]);
                    const curLocalUrl = window.URL.createObjectURL(blob);

                    setState({
                        localUrl: curLocalUrl,
                        success: true,
                        filename: defaultFilename || '',
                        isPDF: curFileIsPdf
                    });
                } catch (error) {
                    setState({
                        success: false,
                        error
                    });
                }

                setState({
                    isDownloading: false
                });
            }
        }

        getImgUrl();

        return () => {
            if (localUrl) {
                window.URL.revokeObjectURL(localUrl);
            }
        };
    });

    return (
        <div
            className="file-uploader-component-root"
            style={{
                backgroundImage: getBackgroundImageUrl()
            }}>
            {success ? (
                <>
                    <div className={classlist('file-select-success', isMobile ? 'mobile-success' : '')}>
                        {!isPDF && <Icon type="eye" onClick={preview} className="eye-icon" />}
                        <Icon type="delete" onClick={handleDel} />
                    </div>
                    {isPDF && <div className="file-name">{filename}</div>}
                </>
            ) : (
                <FileInput
                    onChange={fileChange}
                    uploading={isUploading}
                    downloading={isDownloading}
                    progress={progress}
                    error={error}
                    allow={getAcceptsConfig(type)}
                    isMobile={isMobile}
                />
            )}
        </div>
    );
};

export default FileUploader;
